import { Editor, HistoryEditor } from '../../core';
import type { EditorNormalizeOptions } from 'slate';
import type { DeepLinkPluginOptions } from './types';

const automaticUpdates = new WeakSet<Editor>();

export function withoutTrackingStudyDeepLinkChanges(editor: Editor, update: () => void): void {
  const wasAutomatic = automaticUpdates.has(editor);
  automaticUpdates.add(editor);
  try {
    update();
  } finally {
    if (!wasAutomatic) automaticUpdates.delete(editor);
  }
}

function getStudySmids(editor: Editor): Set<string> {
  return new Set(
    Array.from(Editor.nodes(editor, { at: [] })).flatMap(([node]) =>
      node.type === 'deepLink' && node.variant === 'study' ? [node.context.studySmid] : []
    )
  );
}

export function trackStudyDeepLinkChanges(
  editor: Editor,
  { onStudyDeepLinkRemove, onStudyDeepLinkRestore }: DeepLinkPluginOptions
): void {
  if (onStudyDeepLinkRemove == null && onStudyDeepLinkRestore == null) return;

  let isTracking = false;
  let before: Set<string> | undefined;

  function reconcile() {
    if (isTracking || !Editor.isNormalizing(editor) || before == null) return;
    const previous = before;
    before = undefined;
    const after = getStudySmids(editor);
    previous.forEach((smid) => {
      if (!after.has(smid)) onStudyDeepLinkRemove?.(smid);
    });
    after.forEach((smid) => {
      if (!previous.has(smid)) onStudyDeepLinkRestore?.(smid);
    });
  }

  // Compare completed edits, including normalization. This also handles removal/restoration of a
  // containing paragraph and avoids treating temporary node replacement as deletion of the study.
  function track<Args extends unknown[]>(edit: (...args: Args) => void) {
    return (...args: Args): void => {
      if (isTracking || automaticUpdates.has(editor)) return edit(...args);
      before ??= getStudySmids(editor);
      isTracking = true;
      try {
        edit(...args);
      } finally {
        isTracking = false;
        reconcile();
      }
    };
  }

  // An outer plugin/command may batch edits with Editor.withoutNormalizing. Keep the original
  // comparison until that batch's final normalization, rather than recording its interim state.
  const { normalize } = editor;
  editor.normalize = (options?: EditorNormalizeOptions) => {
    const wasTracking = isTracking;
    isTracking = true;
    try {
      normalize(options);
    } finally {
      isTracking = wasTracking;
      reconcile();
    }
  };

  editor.deleteBackward = track(editor.deleteBackward);
  editor.deleteForward = track(editor.deleteForward);
  editor.deleteFragment = track(editor.deleteFragment);
  editor.insertText = track(editor.insertText);
  editor.insertFragment = track(editor.insertFragment);
  // Transforms.delete is also used by replacement/dictation commands.
  editor.delete = track(editor.delete);
  if (HistoryEditor.isHistoryEditor(editor)) {
    editor.undo = track(editor.undo);
    editor.redo = track(editor.redo);
  }
}
